import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import useUserSession from 'hooks/useUserSession'
import { getMeFactorConfiguration } from 'api/factors'
import { qkGetFactorConfiguration } from 'constants/QueryKeys/factor'
import { getTokenContent, isDefined } from 'tools/common'
import { currencyDropDownOptions, getEnabledCurrencyDropDownOptions } from 'constants/options'
import {
  hasActiveCurrencyFilterForUser,
  loadActiveCurrencyFilter,
  loadLastKnownCurrencyFilter,
  saveActiveCurrencyFilter,
  saveLastKnownCurrencyFilter,
} from './currencyFilterStorage'

type CurrencyFilterContextProps = {
  currentCurrencyFilter: string | undefined
  setCurrentCurrencyFilter: Dispatch<SetStateAction<string | undefined>>
}

// Keeps the selected value tied to the userId it was restored/set for, so a validation effect
// can tell "this is the current user's own restored value" apart from "the previous user's value,
// still sitting in state while the handover to a new userId is in progress".
type CurrencyFilterState = {
  userId: string
  value: string | undefined
}

const CurrencyFilterContext = createContext<CurrencyFilterContextProps | undefined>(undefined)

export const useCurrencyFilterContext = (): CurrencyFilterContextProps => {
  const context = useContext(CurrencyFilterContext)
  if (!context) {
    throw new Error('useCurrencyFilterContext must be used within a CurrencyFilterProvider')
  }
  return context
}

export const CurrencyFilterProvider = ({ children }: { children: ReactNode }) => {
  const [filterState, setFilterState] = useState<CurrencyFilterState | undefined>(undefined)
  const { _token: token } = useUserSession()
  const userId = isDefined(token) ? getTokenContent(token)?.userId : undefined
  const currentCurrencyFilter = filterState?.value

  const { data: factorConfigurationResponse } = useQuery({
    queryKey: qkGetFactorConfiguration(),
    queryFn: () => getMeFactorConfiguration(),
    enabled: isDefined(token),
  })
  const enabledCurrencies = factorConfigurationResponse?.factorConfiguration.enabledCurrencies

  const setCurrentCurrencyFilter: Dispatch<SetStateAction<string | undefined>> = useCallback(
    (valueOrUpdater) => {
      setFilterState((prev) => {
        if (!isDefined(userId)) return prev
        const prevValue = isDefined(prev) && prev.userId === userId ? prev.value : undefined
        const nextValue =
          typeof valueOrUpdater === 'function'
            ? (valueOrUpdater as (prevState: string | undefined) => string | undefined)(prevValue)
            : valueOrUpdater
        saveActiveCurrencyFilter(userId, nextValue)
        saveLastKnownCurrencyFilter(userId, nextValue)
        return { userId, value: nextValue }
      })
    },
    [userId]
  )

  // Runs on every login (fresh or from a reload) and whenever a different user takes over this
  // same tab. If this tab already has a value for that user, keep it; otherwise recover the last
  // value known for them on this browser, falling back to "All" when there is none.
  useEffect(() => {
    if (!isDefined(userId)) return
    if (hasActiveCurrencyFilterForUser(userId)) {
      setFilterState({ userId, value: loadActiveCurrencyFilter(userId) })
      return
    }
    const recovered = loadLastKnownCurrencyFilter(userId)
    saveActiveCurrencyFilter(userId, recovered)
    setFilterState({ userId, value: recovered })
  }, [userId])

  useEffect(() => {
    if (!isDefined(userId) || !isDefined(filterState) || filterState.userId !== userId) return
    if (!isDefined(factorConfigurationResponse) || !isDefined(filterState.value)) return
    const filterEnabled = factorConfigurationResponse.factorConfiguration.enableCurrencyFilter
    const enabledOptions = getEnabledCurrencyDropDownOptions(enabledCurrencies, currencyDropDownOptions)
    const stillSelectable = enabledOptions.some((option) => option.value === filterState.value)
    if (!filterEnabled || !stillSelectable) {
      setCurrentCurrencyFilter(undefined)
    }
  }, [factorConfigurationResponse, enabledCurrencies, filterState, userId, setCurrentCurrencyFilter])

  const value = useMemo(
    () => ({
      currentCurrencyFilter,
      setCurrentCurrencyFilter,
    }),
    [currentCurrencyFilter, setCurrentCurrencyFilter]
  )

  return <CurrencyFilterContext.Provider value={value}>{children}</CurrencyFilterContext.Provider>
}
